import { Divider, Icon, Stack, styled, Tooltip, Typography } from '@mui/material';
import { SchoolVerificationStatus, useGetSchoolQuery, UserRolePermission } from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import {
  ApplicationsIcon,
  AssessmentsIcon,
  AttendanceIcon,
  Attention2Icon,
  CompanyIcon,
  ConductIcon,
  DashboardIcon,
  EventsIcon,
  EyeIcon,
  GridViewIcon,
  GroupsIcon,
  InvoiceIcon,
  MailIcon,
  ParentIcon,
  PlannerIcon,
  ReportsIcon,
  SettingsIcon,
  SimpleButton,
  StaffIcon,
  StudentIcon,
  UserRoleIcon,
} from '@schooly/style';
import React, { FC, PropsWithChildren, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMatch, useNavigate } from 'react-router';
import { NavLink } from 'react-router-dom';

import {
  allowRenderApplications,
  allowRenderAssessments,
  allowRenderAttendance,
  allowRenderCompanies,
  allowRenderConduct,
  allowRenderDashboard,
  allowRenderEvents,
  allowRenderGroups,
  allowRenderMessages,
  allowRenderParents,
  allowRenderPayableFees,
  allowRenderSchoolLegalEntities,
  allowRenderSchoolProducts,
  allowRenderStaff,
  allowRenderStudents,
  allowRenderUserRoles,
} from '../../helpers/renderRoutesByPermissions';
import { useSchool } from '../../hooks/useSchool';
import { useTimetablePermissions } from '../../hooks/useTimetablePermissions';
import useUserCounts from '../../hooks/useUserCounts';
import useAppLocation from '../../hooks/useAppLocation';
import { SIDENAV } from '@schooly/constants/test-ids';

const NavMenu: FC<{ isCollapsed: boolean }> = ({ isCollapsed }) => {
  const { isSchoolAdmin } = useSchool();
  const { userCounts } = useUserCounts();
  const { formatMessage } = useIntl();
  const { permissions, schoolId = '' } = useAuth();
  const { canManageTimetables } = useTimetablePermissions();

  const { data: currentSchool } = useGetSchoolQuery(schoolId, {
    enabled: !!schoolId && isSchoolAdmin,
  });

  const navigate = useNavigate();

  const isSignUpsPath = useMatch('/signups/*');

  const { non_member_adults = 0, application = 0 } = userCounts || {};

  const showAnnualPlanner =
    permissions.includes('planner_viewer') &&
    (['assessment_viewer', 'event_viewer'] as UserRolePermission[]).some((perm) =>
      permissions.includes(perm),
    );

  const showPeopleSection = (
    ['student_viewer', 'parent_viewer', 'staff_viewer'] as UserRolePermission[]
  ).find((perm) => permissions.includes(perm));

  const showPayableFees = allowRenderPayableFees(permissions);
  const showApplications = allowRenderApplications(permissions);
  const showDashboard = allowRenderDashboard(permissions);
  const showActivitySection = (
    [
      'message_viewer',
      'assessment_viewer',
      'conduct_viewer',
      'attendance_viewer',
      'group_viewer',
    ] as UserRolePermission[]
  ).find((perm) => permissions.includes(perm));

  const showUserRoles = allowRenderUserRoles(permissions);
  const hasTimetablesAccess = canManageTimetables;
  const hasLegalEntitiesAccess = allowRenderSchoolLegalEntities(permissions);
  const hasSchoolProductsAccess = allowRenderSchoolProducts(permissions);
  const showSchoolSettings =
    isSchoolAdmin || hasTimetablesAccess || hasLegalEntitiesAccess || hasSchoolProductsAccess;
  const showConfigurationSection = showSchoolSettings || showUserRoles;
  const showInvoicingSection = showPayableFees;

  const { applicationsIndicatorContent, staffIndicatorContent } = useMemo(() => {
    const applicationViewer = permissions.includes('application_viewer');
    const staffManager =
      permissions.includes('staff_manager') && permissions.includes('employment_manager');

    const applicationsIndicatorContent =
      applicationViewer && !!application ? (
        <Stack gap={1} alignItems="flex-start">
          <Typography>{formatMessage({ id: 'applications-thereAreApplications' })}</Typography>
          <SimpleButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/applications');
            }}
            startIcon={<EyeIcon />}
          >
            {formatMessage({ id: 'staff-ViewList' })}
          </SimpleButton>
        </Stack>
      ) : undefined;
    const staffIndicatorContent =
      staffManager && !!non_member_adults ? (
        <Stack gap={1} alignItems="flex-start">
          <Typography>{formatMessage({ id: 'staff-thereAreProfiles' })}</Typography>
          <SimpleButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              navigate('/staff/new');
            }}
            startIcon={<EyeIcon />}
          >
            {formatMessage({ id: 'staff-ViewList' })}
          </SimpleButton>
        </Stack>
      ) : undefined;

    return { staffIndicatorContent, applicationsIndicatorContent };
  }, [permissions, application, formatMessage, non_member_adults, navigate]);

  const schoolNotVerified =
    !!currentSchool && currentSchool.verification_status === SchoolVerificationStatus.NotRequested;

  return (
    <>
      <Section isCollapsed={isCollapsed}>
        {showDashboard && (
          <NavItem
            isCollapsed={isCollapsed}
            link="/dashboard"
            titleTextId="dashboard-Title"
            icon={<DashboardIcon />}
            testId={SIDENAV.DASHBOARD}
          />
        )}
        {showAnnualPlanner && (
          <NavItem
            isCollapsed={isCollapsed}
            link="/planner"
            icon={<PlannerIcon />}
            titleTextId="section-AnnualPlanner"
            testId={SIDENAV.ANNUAL_PLANNER}
          />
        )}
      </Section>

      {showPeopleSection && (
        <>
          <SectionTitle isCollapsed={isCollapsed}>
            <FormattedMessage id="section-People" />
          </SectionTitle>
          <Section isCollapsed={isCollapsed}>
            {showApplications && (
              <NavItem
                isCollapsed={isCollapsed}
                link="/applications"
                titleTextId="applications-Title-plural"
                icon={<ApplicationsIcon />}
                indicatorTooltip={applicationsIndicatorContent}
              />
            )}
            {allowRenderStudents(permissions) && (
              <NavItem
                isCollapsed={isCollapsed}
                link="/students"
                icon={<StudentIcon />}
                titleTextId="section-Students"
                testId={SIDENAV.STUDENTS}
              />
            )}
            {allowRenderParents(permissions) && (
              <NavItem
                isCollapsed={isCollapsed}
                link="/parents"
                icon={<ParentIcon />}
                titleTextId="section-Parents"
                testId={SIDENAV.PARENTS}
              />
            )}
            {allowRenderStaff(permissions) && (
              <NavItem
                isCollapsed={isCollapsed}
                link="/staff"
                icon={<StaffIcon />}
                titleTextId="section-Staff"
                indicatorTooltip={staffIndicatorContent}
                testId={SIDENAV.STAFF}
              />
            )}
            {allowRenderCompanies(permissions) && (
              <NavItem
                isCollapsed={isCollapsed}
                link="/companies"
                icon={<CompanyIcon />}
                titleTextId="section-Companies"
                testId={SIDENAV.COMPANIES}
              />
            )}
          </Section>
        </>
      )}
      {showActivitySection && (
        <>
          <SectionTitle isCollapsed={isCollapsed}>
            <FormattedMessage id="section-Activity" />
          </SectionTitle>
          <Section isCollapsed={isCollapsed}>
            {allowRenderGroups(permissions) && (
              <NavItem
                isCollapsed={isCollapsed}
                link="/groups"
                icon={<GroupsIcon />}
                titleTextId="section-Groups"
                testId={SIDENAV.GROUPS}
              />
            )}
            {allowRenderMessages(permissions) && (
              <NavItem
                isCollapsed={isCollapsed}
                link="/messages"
                icon={<MailIcon />}
                titleTextId="section-Messages"
                testId={SIDENAV.MESSAGES}
              />
            )}
            {allowRenderAssessments(permissions) && (
              <>
                <NavItem
                  isCollapsed={isCollapsed}
                  link="/assessments"
                  icon={<AssessmentsIcon />}
                  titleTextId="section-Assessments"
                  testId={SIDENAV.ASSESSMENTS}
                />
                <NavItem
                  isCollapsed={isCollapsed}
                  link="/markbook"
                  icon={<GridViewIcon />}
                  titleTextId="section-Markbook"
                  testId={SIDENAV.MARKBOOK}
                />
                <NavItem
                  isCollapsed={isCollapsed}
                  link="/reports"
                  icon={<ReportsIcon />}
                  titleTextId="section-Reports"
                  testId={SIDENAV.REPORTS}
                />
              </>
            )}

            {allowRenderAttendance(permissions) && (
              <NavItem
                isCollapsed={isCollapsed}
                link="/attendance"
                icon={<AttendanceIcon />}
                titleTextId="section-Attendance"
                testId={SIDENAV.ATTENDANCE}
              />
            )}

            {allowRenderConduct(permissions) && (
              <NavItem
                isCollapsed={isCollapsed}
                link="/conduct"
                icon={<ConductIcon />}
                titleTextId="section-Conduct"
                testId={SIDENAV.CONDUCT}
              />
            )}
            {allowRenderEvents(permissions) && (
              <NavItem
                isCollapsed={isCollapsed}
                link={isSignUpsPath ? '/signups' : '/events'}
                icon={<EventsIcon />}
                titleTextId="section-EventsAndSignUps"
                testId={SIDENAV.EVENTS}
              />
            )}
          </Section>
        </>
      )}
      {showInvoicingSection && (
        <>
          <SectionTitle isCollapsed={isCollapsed}>
            <FormattedMessage id="section-Invoicing" />
          </SectionTitle>
          <Section isCollapsed={isCollapsed}>
            {showPayableFees && (
              <NavItem
                isCollapsed={isCollapsed}
                link="/payablefees"
                icon={<InvoiceIcon />}
                titleTextId="section-payableFees"
                testId={SIDENAV.PAYABLE_FEES}
              />
            )}
          </Section>
        </>
      )}

      {showConfigurationSection && (
        <>
          <SectionTitle isCollapsed={isCollapsed}>
            <FormattedMessage id="section-Configuration" />
          </SectionTitle>
          <Section isCollapsed={isCollapsed}>
            {showSchoolSettings && (
              <NavItem
                isCollapsed={isCollapsed}
                link="/settings"
                icon={<SettingsIcon />}
                titleTextId="section-SchoolSettings"
                testId={SIDENAV.SETTINGS}
                indicatorTooltip={
                  schoolNotVerified
                    ? formatMessage({ id: 'school-updateSchoolDetails' })
                    : undefined
                }
                indicatorColor="warning.main"
                indicator={
                  <Icon
                    sx={(theme) => ({
                      '.svg-icon': {
                        path: { fill: theme.palette.warning.main },
                        ':hover path': { fill: theme.palette.warning.main },
                      },
                    })}
                  >
                    <Attention2Icon className="reset-svg-currentColor" />
                  </Icon>
                }
              />
            )}

            {showUserRoles && (
              <NavItem
                isCollapsed={isCollapsed}
                link="/userroles"
                icon={<UserRoleIcon />}
                titleTextId="section-UserRoles"
                testId={SIDENAV.USERROLES}
              />
            )}
          </Section>
        </>
      )}
    </>
  );
};

type NavItemProps = {
  isCollapsed: boolean;
  link: string;
  icon: React.ReactNode;
  titleTextId: string;
  testId?: string;
  indicator?: React.ReactNode;
  indicatorTooltip?: React.ReactNode;
  indicatorColor?: string;
};

const NavItem: FC<NavItemProps> = ({
  link,
  icon,
  isCollapsed,
  titleTextId,
  testId,
  indicatorTooltip,
  indicatorColor = 'success.main',
  indicator = <Stack borderRadius={2} width={8} height={8} bgcolor={indicatorColor} />,
}) => {
  const { pathname } = useAppLocation();

  const hasIndicator = !!indicatorTooltip;

  const renderContent = ({ isActive }: { isActive: boolean }) => {
    if (isCollapsed) {
      return (
        <Tooltip
          placement="right"
          arrow={false}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, -10],
                  },
                },
              ],
            },
          }}
          componentsProps={{
            tooltip: {
              sx: (theme) => ({
                borderRadius: theme.spacing(0.5),
                padding: theme.spacing(1),
              }),
            },
          }}
          disableInteractive={!indicatorTooltip}
          title={
            <Stack>
              <Typography>
                <FormattedMessage id={titleTextId} />
              </Typography>
              {!!indicatorTooltip && <Divider sx={{ my: 1 }} />}
              {indicatorTooltip}
            </Stack>
          }
        >
          <Stack
            sx={(theme) => ({
              position: 'relative',
              justifyContent: 'center',
              alignItems: 'center',
              ml: 0.5,
              width: theme.spacing(4.5),
              height: theme.spacing(4.5),
              borderRadius: theme.spacing(1),
              backgroundColor: isActive ? theme.palette.common.light2 : undefined,
              '.nav-icon': {
                transition: 'all .2s',
                color: isActive ? theme.palette.primary.main : theme.palette.common.grey4,
              },

              '&:hover .nav-icon': {
                color: theme.palette.primary.main,
              },
            })}
          >
            <Typography variant="h2" className="nav-icon">
              {icon}
            </Typography>
            {indicatorTooltip && (
              <Stack
                borderRadius={2}
                position="absolute"
                right={4}
                top={4}
                width={8}
                height={8}
                bgcolor={indicatorColor}
              />
            )}
          </Stack>
        </Tooltip>
      );
    }

    return (
      <Stack
        sx={(theme) => ({
          transition: 'all .2s',
          backgroundColor: isActive ? theme.palette.common.light2 : undefined,
          padding: theme.spacing(0.75),
          borderRadius: theme.spacing(1.25),
          '.nav-icon': {
            transition: 'all .2s',
            color: isActive ? theme.palette.primary.main : theme.palette.common.grey4,
          },
          '.nav-label': {
            transition: 'all .2s',
            color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
          },

          '&:hover .nav-icon, &:hover .nav-label': {
            color: theme.palette.primary.main,
          },
        })}
      >
        <Stack
          flexDirection="row"
          position="relative"
          gap={1}
          alignItems="center"
          pr={hasIndicator ? 2 : 0}
        >
          <Stack width={22}>
            <Typography variant="h2" className="nav-icon">
              {icon}
            </Typography>
          </Stack>

          <Typography variant="h3" noWrap className="nav-label">
            <FormattedMessage id={titleTextId} />
          </Typography>

          {indicatorTooltip && (
            <Tooltip
              componentsProps={{
                tooltip: {
                  sx: (theme) => ({
                    padding: theme.spacing(1.25),
                  }),
                },
              }}
              title={indicatorTooltip}
            >
              <Stack
                height={22}
                position="absolute"
                right={0}
                justifyContent="center"
                alignItems="flex-end"
                width={16}
              >
                {indicator}
              </Stack>
            </Tooltip>
          )}
        </Stack>
      </Stack>
    );
  };

  return (
    <NavLink end data-test-id={testId} to={{ pathname: link }} state={{ prevPathname: pathname }}>
      {renderContent}
    </NavLink>
  );
};

const SectionTitle: FC<PropsWithChildren<{ isCollapsed?: boolean }>> = ({
  children,
  isCollapsed,
}) => {
  if (isCollapsed) return <Divider sx={(theme) => ({ margin: theme.spacing(2, 0) })} />;
  return (
    <Typography variant="h4" sx={(theme) => ({ margin: theme.spacing(0, 0, 0.5, 1) })}>
      {children}
    </Typography>
  );
};

const Section = styled(Stack)<{ isCollapsed: boolean }>(({ theme, isCollapsed }) => ({
  gap: isCollapsed ? undefined : theme.spacing(0.25),
  marginBottom: isCollapsed ? undefined : theme.spacing(2.5),
}));

export default NavMenu;
